import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Add16Regular } from "@fluentui/react-icons";
import { useMemo, useState, type KeyboardEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListFilterMenu, ListSearch, ListToolbar } from "@/components/list-toolbar";
import type { IntakeItem, IntakeSource, IntakeStatus } from "@/engine/types";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { IntakeTriageDialog } from "@/features/intake/IntakeTriageDialog";
import { QuickIntakeModal } from "@/features/intake/QuickIntakeModal";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { formatDate } from "@/lib/date-format";
import {
  INTAKE_PRIORITY_LABEL,
  INTAKE_SOURCE_LABEL,
  INTAKE_SOURCES,
  INTAKE_STATUS_LABEL,
  INTAKE_STATUSES,
  INTAKE_TYPE_LABEL,
  intakeCode,
  intakeProgress,
  isOpenIntake,
} from "@/store/intake-ops";
import { useProjectStore } from "@/store/project-store";
import { contentShell, listBody, shellGutter } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";

type View = "table" | "board";
type SourceFilter = "all" | IntakeSource;
type StatusFilter = "all" | IntakeStatus;

const BOARD_COLUMNS: Array<{ id: string; label: string; statuses: IntakeStatus[] }> = [
  { id: "pending", label: "미심사", statuses: ["pending"] },
  { id: "reviewing", label: "심사중", statuses: ["reviewing"] },
  { id: "accepted", label: "승인", statuses: ["accepted"] },
  { id: "closed", label: "반려 / 보류", statuses: ["rejected", "deferred"] },
];

const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

const useStyles = makeStyles({
  page: { ...contentShell, display: "flex", flexDirection: "column", height: "100%", minHeight: 0, backgroundColor: tokens.colorNeutralBackground2 },
  body: { ...listBody, padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}` },
  row: {
    cursor: "pointer",
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  codeCell: { width: "1%", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: tokens.colorNeutralForeground2 },
  shrink: { width: "1%", whiteSpace: "nowrap" },
  titleCell: { maxWidth: 0, width: "100%", minWidth: "240px" },
  muted: { color: tokens.colorNeutralForeground3 },
  urgent: { color: tokens.colorPaletteRedForeground1 },
  board: { display: "grid", gridTemplateColumns: "repeat(4, minmax(220px, 1fr))", gap: tokens.spacingHorizontalM, alignItems: "start", paddingTop: tokens.spacingVerticalM },
  column: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, minWidth: 0 },
  columnHead: { display: "flex", justifyContent: "space-between", color: tokens.colorNeutralForeground1 },
  cardTitle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: tokens.spacingVerticalS,
    textAlign: "left",
    cursor: "pointer",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    fontFamily: "inherit",
    color: "inherit",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
});

function sortIntakes(list: IntakeItem[]): IntakeItem[] {
  return [...list].sort(
    (a, b) =>
      Number(!isOpenIntake(a)) - Number(!isOpenIntake(b)) ||
      (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
      (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0),
  );
}

export function ProjectIntakePage() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const surface = usePlanSurface();
  const [params, setParams] = useSearchParams();
  const intakes = useProjectStore((s) => s.project.intakes);
  const execTasks = useProjectStore((s) => s.project.executionTasks);
  const list = useMemo(() => intakes ?? [], [intakes]);
  const [quickOpen, setQuickOpen] = useState(false);
  const [triageId, setTriageId] = useState<string | null>(null);
  usePageAction(null);

  const view: View = params.get("view") === "board" ? "board" : "table";
  const rawSource = params.get("source");
  const source: SourceFilter = (INTAKE_SOURCES as string[]).includes(rawSource ?? "") ? (rawSource as IntakeSource) : "all";
  const rawStatus = params.get("status");
  const status: StatusFilter = (INTAKE_STATUSES as string[]).includes(rawStatus ?? "") ? (rawStatus as IntakeStatus) : "all";
  const query = params.get("q") ?? "";

  const patch = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === "") p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      sortIntakes(
        list.filter(
          (i) =>
            (source === "all" || i.source === source) &&
            (status === "all" || i.status === status) &&
            (!q || `${intakeCode(i.seq)} ${i.title} ${i.description} ${i.author} ${i.organization ?? ""}`.toLowerCase().includes(q)),
        ),
      ),
    [list, source, status, q],
  );
  const counts = useMemo(() => {
    const m = new Map<IntakeStatus, number>();
    for (const i of list) m.set(i.status, (m.get(i.status) ?? 0) + 1);
    return m;
  }, [list]);
  const description = "심사에 통과한 뒤에만 작업과 검수가 생깁니다.";
  const onRowKey = (e: KeyboardEvent<HTMLElement>, id: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setTriageId(id);
    }
  };

  return (
    <div className={styles.page}>
      <ListToolbar
        title="요구사항"
        description={description}
        headingAction={
          surface.editIssue ? (
            <FuiButton appearance="primary" icon={<Add16Regular />} onClick={() => setQuickOpen(true)}>
              요구사항 접수
            </FuiButton>
          ) : undefined
        }
        search={<ListSearch value={query} onChange={(value) => patch({ q: value })} />}
        filters={
          <>
            <ListFilterMenu
              label="출처"
              value={source}
              idleValue="all"
              onChange={(v) => patch({ source: v === "all" ? null : v })}
              options={[{ value: "all" as SourceFilter, label: "전체" }, ...INTAKE_SOURCES.map((v) => ({ value: v as SourceFilter, label: INTAKE_SOURCE_LABEL[v] }))]}
            />
            <ListFilterMenu
              label="상태"
              value={status}
              idleValue="all"
              onChange={(v) => patch({ status: v === "all" ? null : v })}
              options={[
                { value: "all" as StatusFilter, label: "전체" },
                ...INTAKE_STATUSES.map((v) => ({ value: v as StatusFilter, label: `${INTAKE_STATUS_LABEL[v]} ${counts.get(v) ?? 0}` })),
              ]}
            />
          </>
        }
        view={
          <SegmentedControl
            value={view}
            onChange={(v) => patch({ view: v === "table" ? null : v })}
            ariaLabel="보기"
            options={[
              { value: "table" as View, label: "테이블" },
              { value: "board" as View, label: "칸반" },
            ]}
          />
        }
      />

      <div className={styles.body}>
        {visible.length === 0 ? (
          list.length === 0 ? (
            <GhostEmptyState title="접수된 요구사항이 없어요" description={surface.editIssue ? "「요구사항 접수」로 올리면 심사를 거친 뒤에만 일정과 작업에 반영됩니다." : "이 플랜에 접수된 요구사항이 없습니다."} />
          ) : (
            <GhostEmptyState variant="search" title="조건에 맞는 요구사항이 없어요" description="출처나 상태를 바꾸거나 검색어를 지워 보세요." />
          )
        ) : view === "table" ? (
          <div className={table.frame}>
            <table className={table.table} aria-label="요구사항">
              <thead>
                <tr>
                  <th className={table.th}>번호</th>
                  <th className={table.th}>제목</th>
                  <th className={table.th}>상태</th>
                  <th className={table.th}>출처</th>
                  <th className={table.th}>유형</th>
                  <th className={table.th}>우선순위</th>
                  <th className={mergeClasses(table.th, table.num)}>공수(M/M)</th>
                  <th className={mergeClasses(table.th, table.num)}>접수일</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((i) => {
                  const progress = intakeProgress(i, execTasks);
                  return (
                    <tr key={i.id} className={styles.row} tabIndex={0} onClick={() => setTriageId(i.id)} onKeyDown={(e) => onRowKey(e, i.id)} aria-label={`${intakeCode(i.seq)} ${i.title} 심사`}>
                      <td className={mergeClasses(table.td, styles.codeCell)}>{intakeCode(i.seq)}</td>
                      <td className={mergeClasses(table.td, styles.titleCell)} title={i.title}>
                        <span className={table.clip}>{i.title || "제목 없음"}</span>
                      </td>
                      <td className={mergeClasses(table.td, styles.shrink)}>
                        {INTAKE_STATUS_LABEL[i.status]}
                        {progress ? (progress.done ? " · 완료" : " · 진행") : ""}
                      </td>
                      <td className={mergeClasses(table.td, styles.shrink)}>{INTAKE_SOURCE_LABEL[i.source]}</td>
                      <td className={mergeClasses(table.td, styles.shrink)}>{INTAKE_TYPE_LABEL[i.type]}</td>
                      <td className={mergeClasses(table.td, styles.shrink, i.priority === "urgent" && styles.urgent)}>{INTAKE_PRIORITY_LABEL[i.priority]}</td>
                      <td className={mergeClasses(table.td, table.num, i.estimatedEffortMM === undefined && styles.muted)}>{i.estimatedEffortMM ?? "—"}</td>
                      <td className={mergeClasses(table.td, table.num, styles.muted)}>{formatDate(i.createdAt, "numeric")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.board}>
            {BOARD_COLUMNS.map((col) => {
              const items = visible.filter((i) => col.statuses.includes(i.status));
              return (
                <div key={col.id} className={styles.column}>
                  <div className={styles.columnHead}>
                    <span>{col.label}</span>
                    <span className={styles.muted}>{items.length}</span>
                  </div>
                  {items.map((i) => (
                    <button key={i.id} type="button" className={styles.card} onClick={() => setTriageId(i.id)} onKeyDown={(e) => onRowKey(e, i.id)}>
                      <Caption1 className={styles.muted}>
                        {intakeCode(i.seq)} · {INTAKE_SOURCE_LABEL[i.source]} · {INTAKE_TYPE_LABEL[i.type]}
                      </Caption1>
                      <span className={styles.cardTitle} title={i.title}>{i.title || "제목 없음"}</span>
                      <Caption1 className={i.priority === "urgent" ? styles.urgent : styles.muted}>
                        {INTAKE_PRIORITY_LABEL[i.priority]}
                        {i.status === "rejected" || i.status === "deferred" ? ` · ${INTAKE_STATUS_LABEL[i.status]}` : ""}
                      </Caption1>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <QuickIntakeModal open={quickOpen} onClose={() => setQuickOpen(false)} />
      <IntakeTriageDialog intakeId={triageId} onClose={() => setTriageId(null)} />
    </div>
  );
}
